import React, { createContext, FC, useEffect, useMemo, useReducer, useRef } from 'react';
import { filtersPayload, FilterAction, SortingAction, sortingPayload } from './actions';
import {
  filtersReducer,
  filtersInitializer,
  sortReducer,
  sortingInitializer,
  localStorageSortingLocation,
  localStorageFiltersLocation
} from './reducers';
import {
  GlobalPerPageReducerAction,
  globalPerPagePayload,
  globalPerPageInitializer,
  globalPerPageReducer,
  globalPerPageLocation
} from './reducers/pagingReducer';
import {
  globalPageInitializer,
  globalPagePayload,
  globalPageReducer,
  GlobalPageReducerAction
} from './reducers/pageSelectionReducer';

interface IGlobalContext {
  filterValues: filtersPayload[];
  dispatchFilters: React.Dispatch<FilterAction>;
  sortingValues: sortingPayload[];
  dispatchSorting: React.Dispatch<SortingAction>;
  perPageGlobalValues: globalPerPagePayload[];
  dispatchGlobalPerPage: React.Dispatch<GlobalPerPageReducerAction>;
  pageGlobalValues: globalPagePayload[];
  dispatchGlobalPage: React.Dispatch<GlobalPageReducerAction>;
  contentRef: React.RefObject<HTMLDivElement>;
}

export const GlobalContext = createContext<IGlobalContext>({
  filterValues: [],
  dispatchFilters: () => {},
  sortingValues: [],
  dispatchSorting: () => {},
  perPageGlobalValues: [],
  dispatchGlobalPerPage: () => {},
  pageGlobalValues: [],
  dispatchGlobalPage: () => {},
  contentRef: { current: null }
});

interface GlobalContextHandlerProps {}

export const GlobalContextHandler: FC<GlobalContextHandlerProps> = ({ children }): JSX.Element => {
  // https://stackoverflow.com/questions/64547044/persist-localstorage-with-usereducer
  // Maintain state of filters
  const [filterValues, dispatchFilters] = useReducer(filtersReducer, [], filtersInitializer);

  useEffect(() => {
    localStorage.setItem(localStorageFiltersLocation, JSON.stringify(filterValues));
  }, [filterValues]);

  // Maintain state of sorting
  const [sortingValues, dispatchSorting] = useReducer(sortReducer, [], sortingInitializer);

  // Maintain state of "perPage" values
  const [perPageGlobalValues, dispatchGlobalPerPage] = useReducer(
    globalPerPageReducer,
    [],
    globalPerPageInitializer
  );

  // Maintain state of "perPage" values
  const [pageGlobalValues, dispatchGlobalPage] = useReducer(
    globalPageReducer,
    [],
    globalPageInitializer
  );

  useEffect(() => {
    localStorage.setItem(globalPerPageLocation, JSON.stringify(perPageGlobalValues));
  }, [perPageGlobalValues]);

  useEffect(() => {
    localStorage.setItem(localStorageSortingLocation, JSON.stringify(sortingValues));
  }, [sortingValues]);
  const contentRef = useRef<HTMLDivElement>();
  const globalValues: IGlobalContext = useMemo(() => {
    return {
      filterValues: filterValues as filtersPayload[],
      dispatchFilters,
      sortingValues: sortingValues as sortingPayload[],
      dispatchSorting,
      perPageGlobalValues,
      dispatchGlobalPerPage,
      pageGlobalValues,
      dispatchGlobalPage,
      contentRef
    };
  }, [
    filterValues,
    dispatchFilters,
    sortingValues,
    dispatchSorting,
    perPageGlobalValues,
    dispatchGlobalPerPage,
    pageGlobalValues,
    dispatchGlobalPage,
    contentRef
  ]);

  return <GlobalContext.Provider value={globalValues}>{children}</GlobalContext.Provider>;
};
